import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Download } from 'lucide-react';
import { getRegister, getAccounts, downloadRegisterPDF, RegisterEntry } from '../services/apiService';
import { AccountAutocomplete } from '../components/ui/AccountAutocomplete';

const fmtAmount = (value: number, commodity: string): string => {
  const formatted = Math.abs(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sign = value < 0 ? '-' : '';
  return `${sign}${commodity} ${formatted}`;
};

const PAGE_SIZE = 50;

export default function ExtratoPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState<RegisterEntry[]>([]);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [selectedAccount, setSelectedAccount] = useState(searchParams.get('account') || '');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getAccounts()
      .then((res) => {
        setAccounts(res.accounts);
        const fromUrl = searchParams.get('account');
        if (!selectedAccount && !fromUrl && res.accounts.length > 0) {
          setSelectedAccount(res.accounts[0]);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPage = useCallback(async (offset: number, append: boolean) => {
    if (!selectedAccount) return;
    if (offset === 0) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError('');
    try {
      const res = await getRegister({ account: selectedAccount, offset, limit: PAGE_SIZE });
      setEntries(prev => append ? [...prev, ...res.entries] : res.entries);
      setHasMore(res.has_more);
      setTotal(res.total);
    } catch {
      setError('Erro ao carregar extrato');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedAccount]);

  // Reset on account change
  useEffect(() => {
    if (!selectedAccount) return;
    setEntries([]);
    setHasMore(false);
    fetchPage(0, false);
  }, [fetchPage, selectedAccount]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (observerEntries) => {
        if (observerEntries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchPage(entries.length, true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, entries.length, fetchPage]);

  const handleAccountSelect = (value: string) => {
    setSelectedAccount(value);
    setSearchParams(value ? { account: value } : {}, { replace: true });
  };

  const handleDownloadPDF = async () => {
    if (!selectedAccount || downloading) return;
    setDownloading(true);
    try {
      await downloadRegisterPDF(selectedAccount);
    } catch {
      setError('Erro ao baixar PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Extrato</h1>
        <div className="flex items-center gap-3">
          {total > 0 && (
            <span className="text-sm text-gray-500">{entries.length} de {total}</span>
          )}
          <button
            onClick={handleDownloadPDF}
            disabled={!selectedAccount || loading || downloading}
            className="flex items-center gap-1.5 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
            title="Baixar extrato em PDF"
          >
            <Download className="h-4 w-4" />
            PDF
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="flex gap-4">
        <div className="w-80">
          <AccountAutocomplete
            value={selectedAccount}
            accounts={accounts}
            onChange={handleAccountSelect}
            placeholder="Buscar conta..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>

      {!selectedAccount ? (
        <div className="text-center py-12 text-gray-500">Selecione uma conta</div>
      ) : loading ? (
        <div className="text-center py-12 text-gray-500">Carregando...</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Nenhuma transação encontrada</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Descrição</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Contrapartidas</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Valor</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Saldo</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Docs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {entries.map((entry, idx) => (
                <tr key={idx} className="hover:bg-blue-50 even:bg-gray-100">
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{entry.date}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{entry.payee}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {entry.postings && entry.postings.map((p, pi) => (
                      <div key={pi} className="flex justify-between gap-4">
                        <span className="truncate">{p.account}</span>
                        <span className={`tabular-nums whitespace-nowrap ${p.amount < 0 ? 'text-red-600' : 'text-green-700'}`}>
                          {fmtAmount(p.amount, p.commodity || 'BRL')}
                        </span>
                      </div>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <div>
                      <div className={`font-medium whitespace-nowrap ${entry.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmtAmount(entry.amount, entry.commodity || 'BRL')}
                      </div>
                      {entry.accounts && entry.accounts.length > 0 && entry.accounts.some(a => a.account.includes(':')) && (
                        <div className="mt-1 space-y-0.5">
                          {entry.accounts.map((a, ai) => (
                            <div key={ai} className="text-xs text-gray-400 whitespace-nowrap">
                              {a.account.startsWith(selectedAccount + ':') ? a.account.slice(selectedAccount.length + 1) : a.account}{entry.accounts!.length > 1 ? `: ${fmtAmount(a.amount, a.commodity || 'BRL')}` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-right whitespace-nowrap text-gray-600">
                    {entry.running_balance && Object.keys(entry.running_balance).length > 0
                      ? Object.entries(entry.running_balance).map(([commodity, value], bi) =>
                          <div key={bi}>{fmtAmount(value as number, commodity || 'BRL')}</div>
                        )
                      : fmtAmount(0, entry.commodity || 'BRL')
                    }
                  </td>
                  <td className="px-4 py-3 text-center">
                    {entry.docs && entry.docs.length > 0 && (
                      <div className="flex items-center justify-center gap-1">
                        {entry.docs.map((docKey, di) => (
                          <a
                            key={di}
                            href={`/documentos?highlight=${encodeURIComponent(docKey)}`}
                            title={docKey}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <FileText className="h-4 w-4" />
                          </a>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div ref={sentinelRef} className="h-4" />
          {loadingMore && (
            <div className="text-center py-4 text-gray-500 text-sm">Carregando mais...</div>
          )}
        </div>
      )}
    </div>
  );
}
